import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "@/api";
import { errorMessage } from "@/api/client";
import type { ReportType } from "@/api/types";
import {
  Button,
  Card,
  ConfirmDialog,
  ErrorBanner,
  Field,
  HtmlFrame,
  inputClass,
  PageHeader,
} from "@/components/ui";
import { isValidRange, previousWorkWeek } from "@/lib/dates";

export default function ReportsPage() {
  const defaults = previousWorkWeek();
  const [type, setType] = useState<ReportType>("compliance");
  const [startDate, setStartDate] = useState(defaults.startDate);
  const [endDate, setEndDate] = useState(defaults.endDate);
  const [listId, setListId] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const lists = useQuery({
    queryKey: ["recipients"],
    queryFn: api.listRecipients,
  });
  const matchingLists = (lists.data ?? []).filter((l) => l.reportType === type);
  const validRange = isValidRange(startDate, endDate);

  const preview = useMutation({
    mutationFn: () => api.previewReport({ type, startDate, endDate }),
  });
  const send = useMutation({
    mutationFn: () =>
      api.sendReport({
        type,
        startDate,
        endDate,
        ...(listId && { recipientListId: listId }),
      }),
    onSuccess: (r) =>
      setNotice(
        `Sent to ${r.recipientCount} recipient(s). History id: ${r.historyId}`,
      ),
    onSettled: () => setConfirming(false),
  });

  const changeType = (t: ReportType) => {
    setType(t);
    setListId("");
  };

  return (
    <>
      <PageHeader title="Reports" />
      <Card>
        <div className="grid gap-4 md:grid-cols-4">
          <Field label="Type">
            <select
              className={inputClass}
              value={type}
              onChange={(e) => changeType(e.target.value as ReportType)}
            >
              <option value="compliance">Compliance</option>
              <option value="weekly">Weekly</option>
            </select>
          </Field>
          <Field label="Start date">
            <input
              type="date"
              className={inputClass}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </Field>
          <Field label="End date">
            <input
              type="date"
              className={inputClass}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </Field>
          <Field label="Recipient list (optional)">
            <select
              className={inputClass}
              value={listId}
              onChange={(e) => setListId(e.target.value)}
            >
              <option value="">Default recipients</option>
              {matchingLists.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <div className="mt-4 flex gap-2">
          <Button
            variant="secondary"
            disabled={!validRange || preview.isPending}
            onClick={() => preview.mutate()}
          >
            {preview.isPending ? "Rendering…" : "Preview"}
          </Button>
          <Button
            disabled={!validRange || send.isPending}
            onClick={() => {
              setNotice(null);
              setConfirming(true);
            }}
          >
            Send now
          </Button>
        </div>
        {!validRange && (
          <p className="mt-2 text-sm text-red-600">
            Enter a valid range (YYYY-MM-DD) with start on or before end.
          </p>
        )}
      </Card>

      <div className="mt-6">
        <ErrorBanner
          message={
            preview.error
              ? errorMessage(preview.error)
              : send.error
                ? errorMessage(send.error)
                : null
          }
        />
        {notice && (
          <p className="mb-4 rounded bg-green-50 p-3 text-sm text-green-800">
            {notice}
          </p>
        )}
        {preview.data && (
          <>
            <p className="mb-2 text-sm">
              <span className="text-neutral-500">Subject:</span>{" "}
              <strong>{preview.data.subject}</strong>
            </p>
            <HtmlFrame html={preview.data.html} title="Report preview" />
          </>
        )}
      </div>

      {confirming && (
        <ConfirmDialog
          title="Send report now?"
          message={`This will email the ${type} report for ${startDate} to ${endDate} to ${
            listId
              ? `the "${matchingLists.find((l) => l.id === listId)?.name}" list`
              : "the default recipients"
          }.`}
          confirmLabel="Send"
          busy={send.isPending}
          onConfirm={() => send.mutate()}
          onCancel={() => setConfirming(false)}
        />
      )}
    </>
  );
}
